import {
  Button,
  Flags,
  Icons,
  Select,
  SingleDatePicker,
  Sizes,
  TextInput,
  type IFlagProps,
} from '@sede-enterprise/shell-ds-react-framework';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState, type FC } from 'react';
import Loader from 'src/UI/Loader';
import { sendNotification } from 'src/UI/Notification';
import { CET_TIME_ZONE, COMMON_MESSAGES } from 'src/constants/Common';
import {
  GET_BALANCE_BY_PERIOD,
  SEARCH_BALANCE_BY_PERIOD,
  TIME_SERIES_VIEW_IDS,
} from 'src/constants/EUMarkets';
import { get, isRequestCanceled } from 'src/services/GenesisApi';
import { COUNTRY_HEALTH_DATA } from '../../Dashboard/HealthCheck/constants';
import CustomViewSelect from '../CustomViewSelect';
import MarketButtonGroup from '../MarketButtonGroup';
import { SearchValidationModal } from '../MarketPositionNew/style';
import PositionSearchFilters from '../PositionSearchFilters';
import {
  DEFAULT_PERIOD,
  PERIOD_OPTIONS,
  getPeriodColumns,
  getPeriodEndpoint,
  getPeriodRange,
  type PositionPeriod,
} from '../period';
import { BalancesContainer } from './style';
import type { BalanceSeries } from './types';

const MARKET_NODE_ID = 1;
const MIN_SEARCH_CHARACTERS = 3;
const SEARCH_VALIDATION_MESSAGE = 'Please enter at least 3 characters to perform search.';
const nodeKey = (node: BalanceSeries) =>
  `${node.parentNodeId}:${node.parentSourceId}:${node.nodeId}:${node.sourceId}`;
const isChildOf = (child: BalanceSeries, parent: BalanceSeries) =>
  child.parentNodeId === parent.nodeId && child.parentSourceId === parent.sourceId;
const formatValue = (value: number | null | undefined) =>
  value == null ? '\u2014' : Math.round(value).toLocaleString('en-GB');
const formatTagTotal = (value: number | null) =>
  value == null
    ? '\u2014'
    : new Intl.NumberFormat('en-GB', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value);

const EXTRA_FLAG_CODES: Record<string, string> = {
  'czech republic': 'Cz',
  'united kingdom': 'Gb',
  ireland: 'Ie',
  poland: 'Pl',
};

const MarketFlag = ({ name }: { name: string }) => {
  const flagCode =
    EXTRA_FLAG_CODES[name.toLowerCase()] ??
    COUNTRY_HEALTH_DATA.find((country) => country.name.toLowerCase() === name.toLowerCase())
      ?.flagCode;
  const Flag = flagCode ? (Flags as Record<string, FC<IFlagProps>>)[flagCode] : undefined;
  return Flag ? <Flag height={14} width={14} /> : null;
};

const Balances = () => {
  const [period, setPeriod] = useState<PositionPeriod>(DEFAULT_PERIOD);
  const [gasDay, setGasDay] = useState(() =>
    dayjs().tz(CET_TIME_ZONE).format(COMMON_MESSAGES.DATE_YYYY_MM_DD),
  );
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isSearchValidationPopupOpen, setIsSearchValidationPopupOpen] = useState(false);
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const [customViewId, setCustomViewId] = useState<number | null>(null);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [nodes, setNodes] = useState<BalanceSeries[]>([]);
  const [expandedMarkets, setExpandedMarkets] = useState<Set<string>>(new Set());
  const [expandedBalances, setExpandedBalances] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const { fromDate, toDate } = getPeriodRange(gasDay, period);
  const columns = useMemo(() => getPeriodColumns(gasDay, period), [gasDay, period]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setNodes([]);
    setExpandedMarkets(new Set());
    setExpandedBalances(new Set());
    const searching = Boolean(searchText || organizationIds.length);
    const query = new URLSearchParams({ fromDate, toDate });
    if (searching) {
      query.set('searchText', searchText);
      query.set('organizationIds', organizationIds.join(','));
    } else {
      // Fetch the complete hierarchy so every market's balance tags have totals immediately.
      query.set('nodesToFetch', '');
    }
    if (customViewId !== null) {
      query.set('customViewId', String(customViewId));
    }
    const endpoint = getPeriodEndpoint(
      searching ? SEARCH_BALANCE_BY_PERIOD : GET_BALANCE_BY_PERIOD,
      period,
    );
    get(`${endpoint}?${query.toString()}`, { signal: controller.signal })
      .then(({ data }: { data: BalanceSeries[] }) => {
        if (!active) {
          return;
        }
        if (!Array.isArray(data)) {
          throw new TypeError('Invalid balances response.');
        }
        setNodes(data);
        if (searching) {
          setExpandedMarkets(
            new Set(data.filter((node) => node.nodeId === MARKET_NODE_ID).map(nodeKey)),
          );
          setExpandedBalances(
            new Set(data.filter((node) => node.parentNodeId === MARKET_NODE_ID).map(nodeKey)),
          );
        }
      })
      .catch((cause: unknown) => {
        if (active && !isRequestCanceled(cause)) {
          setError(true);
          sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [fromDate, toDate, period, searchText, organizationIds, customViewId, reload]);

  const calculatedNodes = useMemo(
    () => nodes.filter((node) => node.volumeType === 'CALC'),
    [nodes],
  );
  const markets = calculatedNodes.filter((node) => node.nodeId === MARKET_NODE_ID);
  const visibleMarkets = markets.filter(
    (market) => selectedMarkets.length === 0 || selectedMarkets.includes(market.name ?? ''),
  );
  const toggleMarket = (market: BalanceSeries) => {
    const key = nodeKey(market);
    setExpandedMarkets((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const toggleBalance = (market: BalanceSeries, balance: BalanceSeries) => {
    setExpandedMarkets((previous) => new Set(previous).add(nodeKey(market)));
    setExpandedBalances((previous) => {
      const next = new Set(previous);
      const key = nodeKey(balance);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const applySearch = () => {
    const text = searchInput.trim();
    if (text.length < MIN_SEARCH_CHARACTERS) {
      setIsSearchValidationPopupOpen(true);
      return;
    }
    setSearchText(text);
    setReload((value) => value + 1);
  };
  const clearSearch = () => {
    setSearchInput('');
    setSearchText('');
    setReload((value) => value + 1);
  };
  const valueCells = (node: BalanceSeries) => (
    <>
      <td className='total-column'>{formatValue(node.total)}</td>
      {columns.map((column) => (
        <td key={column.key}>
          {formatValue(node[column.key as `h${number}` | `d${number}` | `m${number}`])}
        </td>
      ))}
    </>
  );
  const moveGasDay = (days: number) =>
    setGasDay(dayjs(gasDay).add(days, 'day').format(COMMON_MESSAGES.DATE_YYYY_MM_DD));

  return (
    <BalancesContainer
      data-period={period}
      $columnCount={columns.length}
      $columnWidth={period === 'day' ? 90 : 122}
    >
      <div className='filter-row'>
        <div className='filter-field period-gas-day-field'>
          <span className='filter-label'>Period &amp; Gas Day</span>
          <div className='period-gas-day-controls'>
            <div className='period-filter-field'>
              <Select
                aria-label='Period'
                size={Sizes.Small}
                options={PERIOD_OPTIONS}
                value={period}
                optionLabelProp='label'
                showSearch={false}
                allowClear={false}
                data-testid='balances-period-select'
                onChange={(value) => {
                  const option = PERIOD_OPTIONS.find((candidate) => candidate.value === value);
                  if (option) {
                    setPeriod(option.value);
                  }
                }}
              />
            </div>
            <div className='gas-day-controls'>
              <Button
                size='small'
                variant='outlined'
                iconOnly
                icon={<Icons.ChevronLeft />}
                aria-label='Previous gas day'
                onClick={() => moveGasDay(-1)}
              />
              <div className='gas-day-picker'>
                <SingleDatePicker
                  className='gas-day'
                  size={Sizes.Small}
                  format='DD MMM YYYY'
                  value={dayjs(gasDay)}
                  allowClear={false}
                  onChange={(value) => {
                    if (value) {
                      setGasDay(dayjs(value).format(COMMON_MESSAGES.DATE_YYYY_MM_DD));
                    }
                  }}
                />
              </div>
              <Button
                size='small'
                variant='outlined'
                iconOnly
                icon={<Icons.ChevronRight />}
                aria-label='Next gas day'
                onClick={() => moveGasDay(1)}
              />
            </div>
          </div>
        </div>
        <div className='search-container'>
          <span className='filter-label'>Search</span>
          <div className='search-input-row'>
            <TextInput
              aria-label='Search balances'
              size={Sizes.Small}
              placeholder='Search'
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              suffix={{
                node: (
                  <Button
                    size='small'
                    iconOnly
                    icon={<Icons.Search height={18} width={18} />}
                    aria-label='Apply search'
                    variant='transparent'
                    onClick={applySearch}
                  />
                ),
              }}
            />
            {searchText ? (
              <Button
                size='small'
                className='search-clear-btn'
                variant='outlined'
                onClick={clearSearch}
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>
        <PositionSearchFilters
          organizationIds={organizationIds}
          onOrganizationIdsChange={setOrganizationIds}
        />
        <CustomViewSelect
          timeSeriesView={TIME_SERIES_VIEW_IDS.BALANCES}
          readOnly
          label='Template'
          onViewChange={setCustomViewId}
        />
        <Button
          size='small'
          variant='outlined'
          iconOnly
          icon={<Icons.ArrowCcw />}
          aria-label='Refresh balances'
          disabled={loading}
          onClick={() => setReload((value) => value + 1)}
        />
      </div>
      <MarketButtonGroup selectedMarkets={selectedMarkets} onChange={setSelectedMarkets} />
      <div className='table-area'>
        {error ? (
          <div className='balance-error' role='alert'>
            Unable to load balances. Use Refresh balances to retry.
          </div>
        ) : null}
        {!loading && !error && visibleMarkets.length === 0 ? (
          <p className='empty-message'>No balances data available.</p>
        ) : null}
        {loading ? (
          <output aria-label='Loading balances'>
            <Loader />
          </output>
        ) : null}
        {!loading && !error && visibleMarkets.length > 0 ? (
          <div className='time-series-wrapper'>
            <div className='balances-viewport'>
              <table className='time-series-table balances-table' aria-label='Balances'>
                <thead>
                  <tr>
                    <th className='tree-column' aria-label='Market, balance and position' />
                    <th className='total-column'>Total</th>
                    {columns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleMarkets.map((market) => {
                    const key = nodeKey(market);
                    const open = expandedMarkets.has(key);
                    const balances = calculatedNodes.filter((node) => isChildOf(node, market));
                    return (
                      <React.Fragment key={key}>
                        <tr className='balance-market-row'>
                          <td colSpan={columns.length + 2} className='market-summary-cell'>
                            <div className='market-summary-content balance-market-header'>
                              <button
                                type='button'
                                className='balance-market-toggle'
                                aria-label={`${open ? 'Collapse' : 'Expand'} ${market.name}`}
                                aria-expanded={open}
                                onClick={() => toggleMarket(market)}
                              >
                                {open ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
                                <MarketFlag name={market.name ?? ''} />
                                {market.name}
                              </button>
                              {balances.map((balance) => (
                                <button
                                  type='button'
                                  key={nodeKey(balance)}
                                  className='balance-tag'
                                  aria-label={`${balance.name} balance in ${market.name}`}
                                  title={`${balance.name}: ${formatValue(balance.total)}${balance.total == null ? '' : ' kWh'}`}
                                  aria-pressed={open && expandedBalances.has(nodeKey(balance))}
                                  onClick={() => toggleBalance(market, balance)}
                                >
                                  {balance.name}: {formatTagTotal(balance.total)}
                                  {balance.total == null ? '' : ' kWh'}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                        {open && balances.length === 0 ? (
                          <tr>
                            <td colSpan={columns.length + 2} className='market-summary-cell'>
                              <div className='market-summary-content balance-empty-cell'>
                                No balances available for this market.
                              </div>
                            </td>
                          </tr>
                        ) : null}
                        {open &&
                          balances.map((balance) => {
                            const balanceKey = nodeKey(balance);
                            const balanceOpen = expandedBalances.has(balanceKey);
                            const positions = calculatedNodes.filter((node) =>
                              isChildOf(node, balance),
                            );
                            return (
                              <React.Fragment key={balanceKey}>
                                <tr className='balance-row'>
                                  <td className='tree-column'>
                                    <button
                                      type='button'
                                      className='balance-toggle'
                                      aria-label={`${balanceOpen ? 'Collapse' : 'Expand'} ${balance.name} in ${market.name}`}
                                      aria-expanded={balanceOpen}
                                      onClick={() => toggleBalance(market, balance)}
                                    >
                                      {balanceOpen ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
                                      <span className='balance-badge' aria-hidden='true'>
                                        B
                                      </span>
                                      {balance.name}
                                    </button>
                                  </td>
                                  {valueCells(balance)}
                                </tr>
                                {balanceOpen &&
                                  positions.map((position) => (
                                    <tr key={nodeKey(position)} className='balance-position-row'>
                                      <td className='tree-column'>
                                        <div className='balance-position-label'>
                                          <span className='position-badge' aria-hidden='true'>
                                            P
                                          </span>
                                          <span
                                            className='position-name'
                                            title={position.name ?? undefined}
                                          >
                                            {position.name}
                                          </span>
                                        </div>
                                      </td>
                                      {valueCells(position)}
                                    </tr>
                                  ))}
                                {balanceOpen && positions.length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan={columns.length + 2}
                                      className='market-summary-cell'
                                    >
                                      <div className='market-summary-content balance-empty-cell'>
                                        No positions available for this balance.
                                      </div>
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
      <SearchValidationModal
        open={isSearchValidationPopupOpen}
        closable={false}
        isDraggable
        width={400}
        footer={
          <div className='button-style'>
            <Button onClick={() => setIsSearchValidationPopupOpen(false)} variant='outlined'>
              Close
            </Button>
          </div>
        }
        onClose={() => setIsSearchValidationPopupOpen(false)}
        showHeader={false}
        maskClosable={false}
        style={{ top: '30%' }}
        data-testid='balances-search-validation-modal'
      >
        {SEARCH_VALIDATION_MESSAGE}
      </SearchValidationModal>
    </BalancesContainer>
  );
};

export default Balances;
